import { Link } from "react-router-dom";

export function SectionIntro({ eyebrow, title, description, action }) {
  return (
    <div className="mb-8 flex min-w-0 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-3xl">
        {eyebrow && <p className="eyebrow-chip">{eyebrow}</p>}
        <h2 className="mt-4 text-2xl font-black tracking-tight text-neutral-950 sm:text-4xl">{title}</h2>
        {description && <p className="mt-4 text-base leading-8 text-neutral-600">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatStrip({ items }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="soft-card soft-card-hover p-5">
          <p className="font-display text-2xl font-black tracking-tight text-neutral-950">{item.value}</p>
          <p className="mt-2 text-sm leading-6 text-neutral-600">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

export function DeepCard({ title, description, children, to, label = "Open guide" }) {
  return (
    <article className="soft-card soft-card-hover h-full p-5 sm:p-6">
      <h3 className="text-xl font-black tracking-tight text-neutral-950">{title}</h3>
      {description && <p className="mt-3 text-sm leading-7 text-neutral-600">{description}</p>}
      {children && <div className="mt-4">{children}</div>}
      {to && (
        <Link to={to} className="btn-secondary mt-5 px-4 py-2">
          {label}
        </Link>
      )}
    </article>
  );
}

export function BulletGrid({ title, items }) {
  return (
    <div>
      {title && <h4 className="text-sm font-black text-neutral-950">{title}</h4>}
      <ul className="mt-3 grid gap-2 text-sm leading-6 text-neutral-600">
        {items.map((item) => (
          <li key={item} className="rounded-2xl border border-neutral-200/80 bg-neutral-50 px-4 py-3">{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function PillList({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span key={item} className="data-badge">
          {item}
        </span>
      ))}
    </div>
  );
}

export function LinkPills({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Link key={item.to} to={item.to} className="btn-secondary px-4 py-2">
          {item.label}
        </Link>
      ))}
    </div>
  );
}

export function FAQBlock({ items }) {
  return (
    <section className="site-shell py-12 sm:py-16">
      <SectionIntro eyebrow="FAQ" title="Common technical questions" description="Short answers on robot architecture, deployment limits and research boundaries before the deeper technical sections." />
      <div className="grid gap-4 lg:grid-cols-2">
        {items.map((item) => (
          <article key={item.question} className="soft-card p-5 sm:p-6">
            <h3 className="text-lg font-black text-neutral-950">{item.question}</h3>
            <p className="mt-3 text-sm leading-7 text-neutral-600">{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ResearchTable({ columns, rows }) {
  return (
    <ResponsiveComparisonTable
      columns={columns}
      rows={rows}
      caption="Editorial comparison table"
      ariaLabel="Editorial comparison table"
      minWidth="min-w-[760px]"
    />
  );
}


export function ResponsiveComparisonTable({ columns, rows, caption, ariaLabel, minWidth = "min-w-[900px]" }) {
  const normalizedColumns = columns.map((column, index) => (
    typeof column === "string" ? { key: String(index), label: column } : column
  ));

  const getCell = (row, column, index) => {
    if (Array.isArray(row)) return row[index];
    return row[column.key];
  };

  const getRowKey = (row, index) => {
    if (Array.isArray(row)) return `${row[0]}-${index}`;
    return row.id || row.name || row.title || `${index}`;
  };

  return (
    <div className="rounded-[1.75rem] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
      <div className="overflow-x-auto" role="region" aria-label={ariaLabel || caption || "Comparison table"} tabIndex="0">
        <table className={`w-full ${minWidth} border-collapse text-left text-sm leading-7`} aria-label={ariaLabel || caption}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-950 text-white">
              {normalizedColumns.map((column) => (
                <th key={column.key} scope="col" className="px-3 py-3 align-top table-heading sm:px-4 sm:py-4">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {rows.map((row, rowIndex) => (
              <tr key={getRowKey(row, rowIndex)} className="align-top">
                {normalizedColumns.map((column, columnIndex) => (
                  <td key={`${getRowKey(row, rowIndex)}-${column.key}`} className="px-3 py-3 text-neutral-600 sm:px-4 sm:py-4">
                    <div className={columnIndex === 0 ? "font-black text-neutral-950" : undefined}>
                      {getCell(row, column, columnIndex)}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CompactList({ items }) {
  if (!items?.length) return null;

  return (
    <ul className="grid gap-1.5">
      {items.map((item) => (
        <li key={item} className="leading-6">{item}</li>
      ))}
    </ul>
  );
}
